import { loadSession } from "./session.js";

// Same-origin: the web app and its /api/* functions are one Vercel deployment
// (Decision 1/6), so no base URL configuration is needed here.
const API_BASE = "";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const session = loadSession();
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  if (session) headers.set("Authorization", `Bearer ${session.token}`);

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!response.ok) {
    let error = `http_${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) error = body.error;
    } catch {
      // ignore
    }
    throw new Error(error);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// --- Auth (Revision 4: username + password login, replaces pairing) ---

export function getAccountSalt(username: string): Promise<{ salt: string }> {
  return request(`/api/auth/salt?username=${encodeURIComponent(username)}`, { method: "GET" });
}

export function registerAccount(input: {
  username: string;
  authSecret: string;
  recoveryAuthProof: string;
  wrappedChatKeyPassword: string;
  wrappedChatKeyRecovery: string;
  salt: string;
  deviceType: "web";
  deviceName: string;
  // Revision 7: required for every account after #1 — omit entirely for the
  // very first account on a fresh deploy.
  inviteCode?: string;
}): Promise<{ username: string; deviceId: string; deviceToken: string }> {
  return request("/api/auth/register", { method: "POST", body: JSON.stringify(input) });
}

export function login(input: {
  username: string;
  authSecret: string;
  deviceType: "web";
  deviceName: string;
}): Promise<{ username: string; deviceId: string; deviceToken: string; wrappedChatKeyPassword: string }> {
  return request("/api/auth/login", { method: "POST", body: JSON.stringify(input) });
}

// --- Recovery + device-link (Revision 5, Decisions 4/11/12) ---
//
// Deviation note: the handoff's "Files to touch" table lists this file
// (`web/src/lib/api.ts`) under a "No change expected" bucket shared with
// `Chat.tsx`/`polling.ts`. That's inconsistent with the rest of the same
// table, which explicitly rewrites `Setup.tsx`/`Login.tsx`/`Settings.tsx`
// (all of which need new request functions below) and creates
// `Recovery.tsx` (same) — those pages cannot compile/function without this
// file gaining the corresponding request wrappers. Treated as a drafting
// oversight in the plan and edited accordingly; every existing exported
// function's shape is preserved except `login`'s added `wrappedChatKeyPassword`
// field (required by `web/api/auth/login.ts`'s own Revision 5 rewrite) and
// `registerAccount`'s new required fields (required by `register.ts`'s).

export function getRecoveryStart(username: string): Promise<{ salt: string; wrappedChatKeyRecovery: string }> {
  return request(`/api/auth/recovery-start?username=${encodeURIComponent(username)}`, { method: "GET" });
}

export function confirmRecovery(input: {
  username: string;
  recoveryAuthProof: string;
  newAuthSecret: string;
  newWrappedChatKeyPassword: string;
}): Promise<{ ok: boolean; username: string }> {
  return request("/api/auth/recovery-confirm", { method: "POST", body: JSON.stringify(input) });
}

export function createDeviceLink(input: {
  linkSalt: string;
  pinHash: string;
  wrappedChatKeyForPin: string;
}): Promise<{ linkId: string; expiresAt: number }> {
  return request("/api/auth/device-link/create", { method: "POST", body: JSON.stringify(input) });
}

// --- Device-authorization ("device code") login for `mcp` — approved from
// the `/authorize` screen (see `web/src/pages/Authorize.tsx`). `start`/`poll`
// are only ever called by the CLI itself, never from this web app. ---

export function approveDeviceAuthorization(input: {
  userCode: string;
  wrappedChatKeyForCode: string;
}): Promise<{ ok: boolean }> {
  return request("/api/auth/device-authorize/approve", { method: "POST", body: JSON.stringify(input) });
}

// --- Account invites (Revision 7; any authenticated account may generate one) ---

export interface AccountInvite {
  id: string;
  expiresAt: number;
  consumedAt: number | null;
  consumedByUserId: string | null;
  createdAt: number;
  status: "pending" | "used" | "expired";
}

export function listAccountInvites(): Promise<{ invites: AccountInvite[] }> {
  return request("/api/account-invites", { method: "GET" });
}

export function createAccountInvite(): Promise<{ code: string; expiresAt: number }> {
  return request("/api/account-invites", { method: "POST" });
}

// --- Messages ---

export interface ApiMessage {
  id: string;
  senderDeviceId: string;
  ciphertext: string | null;
  nonce: string | null;
  encrypted: boolean;
  createdAt: number;
  expiresAt: number;
  attachment: {
    id: string;
    filename: string;
    contentType: string | null;
    sizeBytes: number;
    encrypted: boolean;
  } | null;
  updatedAt?: number;
  editedAt?: number | null;
  deletedAt?: number | null;
}

function messagesQuery(params: Record<string, string>): string {
  const q = new URLSearchParams({ ...params, includeDeleted: "1" });
  return `/api/messages?${q.toString()}`;
}

export function listMessages(since: number, limit = 50): Promise<{ messages: ApiMessage[]; deviceId: string }> {
  return request(messagesQuery({ since: String(since), limit: String(limit) }), { method: "GET" });
}

// `before`/no-cursor pagination (history scroll-up, search/attachments full-
// history load, and the initial "most recent page" load) — separate from
// `listMessages` above so `since`'s catch-up call shape (relied on by
// `ws.ts` for live-update re-sync) stays untouched.
export function listMessagesPage(
  cursor: { before: number } | Record<string, never>,
  limit = 50
): Promise<{ messages: ApiMessage[]; deviceId: string }> {
  const params: Record<string, string> = { limit: String(limit) };
  if ("before" in cursor) params.before = String(cursor.before);
  return request(messagesQuery(params), { method: "GET" });
}

export function listChanges(since: number, limit = 200): Promise<{ messages: ApiMessage[]; deviceId: string }> {
  return request(`/api/messages/changes?since=${since}&limit=${limit}`, { method: "GET" });
}

export function editMessage(id: string, input: { ciphertext: string; nonce: string }): Promise<{ message: ApiMessage }> {
  return request(`/api/messages/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  });
}

export function deleteMessage(id: string): Promise<{ message: ApiMessage }> {
  return request(`/api/messages/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// --- Slash commands (plaintext dispatch channel — see
// server/src/commands/registry.ts. Deliberately separate from
// `sendMessage` above: this is the one narrow exception to Bridge's E2EE
// model, used only for messages the user typed starting with "/".) ---

export function runCommand(command: string, args: string): Promise<{ result: string }> {
  return request("/api/commands", { method: "POST", body: JSON.stringify({ command, args }) });
}

export function sendMessage(input: {
  ciphertext?: string;
  nonce?: string;
  encrypted?: boolean;
  plaintext?: string;
  attachment?: {
    id: string;
    url: string;
    pathname: string;
    filename: string;
    contentType?: string;
    sizeBytes: number;
  };
}): Promise<{ id: string; createdAt: number; expiresAt: number }> {
  return request("/api/messages", { method: "POST", body: JSON.stringify(input) });
}

// --- Files (Revision 6, Decision 3b/1b: S3 presigned URLs, replacing
// `@vercel/blob/client`'s `upload()` helper, which no longer exists once
// `web/api/files/upload-token.ts` stops speaking Vercel Blob's client-upload
// protocol. This function is new — the handoff's file-touch table grouped
// this file under "No change expected," which doesn't survive
// `files.ts`/`web/package.json` genuinely dropping `@vercel/blob`; treated
// the same way Revision 5's own deviation note above handled an analogous
// table inconsistency. `Chat.tsx` now calls this + a plain `fetch` PUT
// instead of `upload()`.) ---

export function requestUploadUrl(input: {
  filename: string;
  sizeBytes: number;
  contentType?: string;
}): Promise<{ attachmentId: string; key: string; uploadUrl: string; expiresInSec: number }> {
  return request("/api/files/upload-token", { method: "POST", body: JSON.stringify(input) });
}

export function getAttachment(id: string): Promise<{
  id: string;
  url: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number;
  encrypted: boolean;
}> {
  return request(`/api/files/${encodeURIComponent(id)}`, { method: "GET" });
}

// --- Link preview (chat's URL-in-message preview cards; server fetches the
// target URL itself and returns clean metadata — see
// server/src/routes/link-preview.ts. Only the bare URL string is ever sent
// here, never message content/ciphertext.) ---

export interface LinkPreview {
  title: string | null;
  description: string | null;
  image: string | null;
  /** Same-origin signed path from the server — prefer over `image` for <img>. */
  imageProxy: string | null;
}

export function getLinkPreview(url: string): Promise<LinkPreview> {
  return request(`/api/link-preview?url=${encodeURIComponent(url)}`, { method: "GET" });
}

// --- Settings ---

export function getSettings(): Promise<{ notificationPreviewWhenE2eeOff: boolean; e2eeRequired: boolean }> {
  return request("/api/settings", { method: "GET" });
}

export function patchSettings(notificationPreviewWhenE2eeOff: boolean): Promise<{
  notificationPreviewWhenE2eeOff: boolean;
  e2eeRequired: boolean;
}> {
  return request("/api/settings", {
    method: "PATCH",
    body: JSON.stringify({ notificationPreviewWhenE2eeOff })
  });
}

export function revokeThisDevice(): Promise<{ ok: boolean }> {
  return request("/api/devices/revoke", { method: "POST" });
}
